import { apiConfig } from "@/config/apiConfig";
import { getAuth0TokenBrowser } from "@/utils/auth0Browser";
import type { RpcBatchRequest, RpcResponse } from "../types/rpc.types";
import { RpcRequestError } from "../errors/RpcRequestError";
import { createRpcRequest, logRpcCall, logRpcError } from "../utils/rpc.helpers";

// Generic batch call
export const callRpcBatch = async <T = unknown>(
  requests: RpcBatchRequest[],
  init?: RequestInit
): Promise<T[]> => {
  const { headers = {}, ...rest } = init || {};
  
  const batchRequest = requests.map((req, index) => 
    createRpcRequest(req.method, req.params, req.id || String(index + 1))
  );

  try {
    const response = await fetch(apiConfig.baseRpcUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...headers as Record<string, string>,
      },
      body: JSON.stringify(batchRequest),
      ...rest,
    });

    const results: RpcResponse<T>[] | undefined = await response.json().catch(() => undefined);

    if (!response.ok) {
      if (Array.isArray(results) && results[0]?.error) {
        const firstError = results[0].error;
        throw new RpcRequestError(
          firstError.message,
          firstError.code,
          firstError.code,
          firstError.data
        );
      }
      throw new RpcRequestError(
        `HTTP batch error: ${response.status} ${response.statusText}`,
        response.status
      );
    }

    if (!results) {
      throw new RpcRequestError(
        `HTTP batch error: ${response.status} ${response.statusText}`,
        response.status
      );
    }
    
    logRpcCall(`batch[${requests.length}]`, requests.map(r => r.method), response.status);
    
    return results.map((result, index) => {
      if (result.error) {
        const request = requests[index];
        logRpcError(request?.method || 'unknown', request?.params, result.error);
        throw new RpcRequestError(
          result.error.message || 'RPC error',
          result.error.code,
          result.error.code,
          result.error.data
        );
      }
      return result.result as T;
    });
    
  } catch (error) {
    logRpcError(`batch[${requests.length}]`, requests.map(r => r.method), error);
    throw error;
  }
};

// Browser batch call with Auth0 token
export const callRpcBatchBrowser = async <T = unknown>(
  requests: RpcBatchRequest[]
): Promise<T[]> => {
  const token = await getAuth0TokenBrowser();
  
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return callRpcBatch<T>(requests, {
    headers,
    credentials: "include"
  });
};
